import { useEffect } from 'react';
import { useSecurityMonitoring } from './use-security-monitoring';
import { isValidationError } from '@/lib/errors';

/**
 * Global error tracking hook
 * Captures unhandled errors and promise rejections
 * Reports to security system for monitoring
 * 
 * NOTE: ValidationErrors are intentionally filtered out as they are
 * expected user-facing errors already handled by UI (toasts)
 */
export const useErrorTracking = () => {
  const { logError } = useSecurityMonitoring();

  useEffect(() => {
    // Filter development errors
    const isDev = import.meta.env.DEV;
    
    // Handle global JavaScript errors
    const handleError = (event: ErrorEvent) => {
      const { error, message, filename, lineno, colno } = event;
      
      // Skip if no error object
      if (!error) {
        console.warn('[Error Tracking] Error event without error object:', message);
        return;
      }

      // Skip localhost/dev errors in production
      if (!isDev && (filename?.includes('localhost') || filename?.includes('127.0.0.1'))) {
        return;
      }

      const syntheticError = error || new Error(message);
      
      logError(syntheticError, 'unhandled_error', {
        route: window.location.pathname,
        userAction: 'unknown',
        additionalInfo: {
          filename,
          lineno,
          colno,
          errorEvent: 'window.onerror'
        }
      });
    };

    // Handle unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const { reason } = event;
      
      // Skip ValidationErrors - these are expected user-facing errors
      // already handled by the UI (toast notifications)
      if (isValidationError(reason)) {
        console.log('[Error Tracking] Skipping ValidationError (handled by UI):', reason.message);
        return;
      }
      
      // Create error from rejection reason
      const error = reason instanceof Error 
        ? reason 
        : new Error(typeof reason === 'string' ? reason : 'Unhandled Promise Rejection');

      // Skip localhost/dev errors
      const stackHasLocalhost = error.stack?.includes('localhost') || error.stack?.includes('127.0.0.1');
      if (!isDev && stackHasLocalhost) {
        return;
      }

      logError(error, 'unhandled_error', {
        route: window.location.pathname,
        userAction: 'promise_rejection',
        additionalInfo: {
          rejectionReason: typeof reason === 'object' ? JSON.stringify(reason) : String(reason),
          errorEvent: 'unhandledrejection'
        }
      });
    };

    // Attach global handlers
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [logError]);
};
